import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FaBrain, FaChartLine, FaShieldAlt, FaRocket, FaPlay, FaFacebook, FaTwitter, FaLinkedin, FaInstagram, FaYoutube, FaTiktok } from "react-icons/fa";
import { useNavigate } from "react-router-dom";

// Move static CSS to external file or index.css
const styles = `
  @keyframes gradient {
    0%, 100% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
  }
  .animate-gradient {
    background-size: 200% auto;
    animation: gradient 3s linear infinite;
  }
  @keyframes css-pulse {
    0%, 100% { opacity: 0.2; transform: scale(1); }
    50% { opacity: 0.4; transform: scale(1.05); }
  }
  @keyframes orbFloat {
    0%, 100% {
      transform: translate(0, 0) scale(1);
      opacity: 0.3;
    }
    50% {
      transform: translate(var(--xOffset), var(--yOffset)) scale(1.2);
      opacity: 0.6;
    }
  }
  .orb-float {
    animation: orbFloat ease-in-out infinite;
  }
`;

const Hero = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  const navigate = useNavigate();
  const rafRef = useRef(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  // Memoized steps
  const steps = useMemo(() => [
    {
      icon: <FaBrain />,
      title: "Capture Chaos",
      desc: "Every potential customer from your website forms, chat widgets, or ad clicks.",
      color: "from-purple-500 to-pink-500"
    },
    {
      icon: <FaChartLine />,
      title: "Score Intelligence",
      desc: "Score them automatically hot (ready to buy), warm (nurture now), or cold (follow up later) using simple rules plus AI brainpower.",
      color: "from-blue-500 to-cyan-500"
    },
    {
      icon: <FaShieldAlt />,
      title: "Detect Shadows",
      desc: "Anomaly radar flags bots and fakes with confidence—protect your campaigns from invisible threats.",
      color: "from-orange-500 to-red-500"
    },
    {
      icon: <FaRocket />,
      title: "Launch Insights",
      desc: "Seamless dashboard + SDK embeds (WordPress/Shopify). Integrate HubSpot, Zapier—white-label for SaaS dreams.",
      color: "from-green-500 to-emerald-500"
    },
  ], []);

  // Reduced number of orbs for performance
  const orbs = useMemo(() => [...Array(3)].map((_, i) => ({
    id: i,
    width: `${80 + Math.random() * 80}px`,
    height: `${80 + Math.random() * 80}px`,
    left: `${Math.random() * 100}%`,
    top: `${Math.random() * 100}%`,
    duration: 15 + Math.random() * 5,
    xOffset: Math.random() * 50 - 25,
    yOffset: Math.random() * 50 - 25,
  })), []);

  // Throttle mousemove event
  const handleMouseMove = useCallback((e) => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(() => {
      setMousePosition({ x: e.clientX / window.innerWidth - 0.5, y: e.clientY / window.innerHeight - 0.5 });
    });
  }, []);

  useEffect(() => {
    if (!prefersReducedMotion) {
      window.addEventListener('mousemove', handleMouseMove);
    }
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [handleMouseMove, prefersReducedMotion]);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefersReducedMotion(mediaQuery.matches);
    const listener = (e) => setPrefersReducedMotion(e.matches);
    mediaQuery.addEventListener('change', listener);
    return () => mediaQuery.removeEventListener('change', listener);
  }, []);

  useEffect(() => {
    if (prefersReducedMotion) return;
    const interval = setInterval(() => {
      if (document.hidden) return;
      setActiveStep((prev) => (prev + 1) % steps.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [steps.length, prefersReducedMotion]);

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-white text-slate-900">
      {/* Background gradients - reduced opacity and blur */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-0 left-1/4 w-64 h-64 bg-purple-200/20 rounded-full blur-xl [animation:css-pulse 4s ease-in-out infinite] [animation-delay:0s]" />
        <div className="absolute bottom-0 right-1/4 w-64 h-64 bg-pink-200/20 rounded-full blur-xl [animation:css-pulse 4s ease-in-out infinite] [animation-delay:1s]" />
      </div>

      {/* Simplified grid with minimal parallax */}
      {!prefersReducedMotion && (
        <motion.div
          className="absolute inset-0 opacity-[0.02] will-change-transform"
          style={{
            backgroundImage: 'linear-gradient(rgba(0,0,0,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(0,0,0,0.03) 1px, transparent 1px)',
            backgroundSize: '40px 40px',
            transform: 'translateZ(0)',
          }}
          animate={{
            x: mousePosition.x * 10,
            y: mousePosition.y * 10,
          }}
          transition={{ type: "spring", stiffness: 100, damping: 20 }}
        />
      )}

      {/* Floating orbs - reduced to 3 */}
      {!prefersReducedMotion && orbs.map((orb) => (
        <div
          key={orb.id}
          className="orb-float absolute rounded-full bg-gradient-to-r from-purple-300/15 to-pink-300/15 blur-lg"
          style={{
            width: orb.width,
            height: orb.height,
            left: orb.left,
            top: orb.top,
            animationDuration: `${orb.duration}s`,
            transform: 'translateZ(0)',
          }}
        />
      ))}

      <div className="relative container mx-auto px-6 max-w-6xl z-10 py-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
          <LeftColumn activeStep={activeStep} steps={steps} setActiveStep={setActiveStep} prefersReducedMotion={prefersReducedMotion} />
          <RightColumn prefersReducedMotion={prefersReducedMotion} />
        </div>
      </div>

      {/* Inject styles - ideally move to index.css */}
      <style>{styles}</style>
    </section>
  );
};

const LeftColumn = React.memo(({ activeStep, steps, setActiveStep, prefersReducedMotion }) => {
  const handleStepClick = useCallback((idx) => setActiveStep(idx), [setActiveStep]);

  const StaticHeadline = React.memo(() => (
    <div className="space-y-4">
      <h1 className="text-4xl md:text-5xl font-black leading-tight tracking-tight">
        <span className="block text-slate-900">Turn Your Leads into</span>
        <span className="block bg-gradient-to-r from-pink-600 via-purple-600 to-cyan-600 bg-clip-text text-transparent animate-gradient">
          Loyal Customers
        </span>
        <span className="block text-slate-900">Intelligently.</span>
      </h1>
      <p className="text-lg md:text-xl text-slate-600 leading-relaxed max-w-xl">
        One private, AI-powered system that helps you capture, score, and act on leads from all your marketing channels in one clean dashboard.
      </p>
    </div>
  ));

  const StaticCTAs = React.memo(() => {
    const navigate = useNavigate();
    const openWaitlist = () => navigate('/waitlist');

    return (
      <motion.div
        initial={prefersReducedMotion ? { opacity: 1, y: 0 } : { opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="flex flex-col sm:flex-row gap-3 pt-4"
      >
        <motion.button
          whileHover={prefersReducedMotion ? false : { scale: 1.03 }}
          whileTap={{ scale: 0.98 }}
          onClick={openWaitlist}
          className="group relative px-6 py-3 rounded-lg font-semibold text-base overflow-hidden shadow-md"
        >
          <div className="absolute inset-0 bg-gradient-to-r from-pink-600 via-purple-600 to-cyan-600 animate-gradient" />
          <div className="absolute inset-0 bg-gradient-to-r from-pink-700 via-purple-700 to-cyan-700 opacity-0 group-hover:opacity-100 transition-opacity" />
          <span className="relative text-white flex items-center gap-2 justify-center">
            Start Free Trial
            <svg className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
            </svg>
          </span>
        </motion.button>

        <motion.button
          whileHover={prefersReducedMotion ? false : { scale: 1.03 }}
          whileTap={{ scale: 0.98 }}
          onClick={openWaitlist}
          className="group relative px-6 py-3 rounded-lg font-semibold text-base bg-slate-100 border border-slate-200 text-slate-900 hover:bg-slate-200 transition-all flex items-center gap-2 justify-center"
        >
          <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center">
            <FaPlay className="text-xs ml-0.5 text-slate-700" />
          </div>
          Watch Demo
        </motion.button>
      </motion.div>
    );
  });

  const StaticSocialProof = React.memo(() => (
    <motion.div
      initial={prefersReducedMotion ? { opacity: 1 } : { opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: 0.7 }}
      className="flex items-center gap-4 pt-4 text-sm text-slate-600"
    >
      <div className="flex -space-x-1">
        {[1, 2, 3].map((i) => (
          <div key={i} className="w-6 h-6 rounded-full bg-gradient-to-br from-purple-400 to-pink-400 border border-white shadow-sm" />
        ))}
      </div>
      <div>
        <p className="text-slate-900 font-medium">199+ teams</p>
        <p>capturing smarter leads</p>
      </div>
    </motion.div>
  ));

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="lg:col-span-7 space-y-6"
    >
      <motion.div
        initial={prefersReducedMotion ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.2 }}
        className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-slate-100 border border-slate-200"
      >
        <span className="relative flex h-2 w-2">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-500 opacity-75"></span>
          <span className="relative inline-flex rounded-full h-2 w-2 bg-green-600"></span>
        </span>
        <span className="text-xs font-medium text-slate-900">Real-Time AI Scoring</span>
      </motion.div>

      <StaticHeadline />

      <motion.div
        initial={prefersReducedMotion ? { opacity: 1, y: 0 } : { opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="relative"
      >
        <div className="relative rounded-xl bg-white border border-slate-200 p-4 shadow-md">
          <div className="absolute inset-0 rounded-xl opacity-10">
            <div className={`absolute inset-0 rounded-xl bg-gradient-to-r ${steps[activeStep].color} blur-lg`} />
          </div>

          <AnimatePresence mode="wait">
            <motion.div
              key={activeStep}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.4 }}
              className="flex items-start gap-4"
            >
              <motion.div
                className={`p-3 bg-gradient-to-br ${steps[activeStep].color} rounded-lg shadow-md flex-shrink-0`}
                whileHover={prefersReducedMotion ? false : { scale: 1.05 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                <div className="text-2xl text-white">{steps[activeStep].icon}</div>
              </motion.div>
              <div className="flex-1 pt-1">
                <h4 className="font-semibold text-xl text-slate-900 mb-1">{steps[activeStep].title}</h4>
                <p className="text-slate-600 text-sm">{steps[activeStep].desc}</p>
              </div>
            </motion.div>
          </AnimatePresence>

          <div className="flex gap-1 mt-4">
            {steps.map((_, idx) => (
              <button
                key={idx}
                onClick={() => handleStepClick(idx)}
                className={`h-1 rounded-full transition-all duration-200 ${
                  idx === activeStep ? 'w-6 bg-slate-900' : 'w-1 bg-slate-300'
                }`}
              />
            ))}
          </div>
        </div>
      </motion.div>

      <StaticCTAs />
      <StaticSocialProof />
    </motion.div>
  );
});

const RightColumn = React.memo(({ prefersReducedMotion }) => {
  const socialIcons = useMemo(() => [
    { Icon: FaFacebook, color: 'from-blue-600 to-blue-700', label: 'Facebook' },
    { Icon: FaTwitter, color: 'from-sky-500 to-sky-600', label: 'Twitter' },
    { Icon: FaLinkedin, color: 'from-blue-700 to-blue-800', label: 'LinkedIn' },
    { Icon: FaInstagram, color: 'from-pink-600 to-purple-600', label: 'Instagram' },
  ], []);

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.5, delay: 0.2 }}
      className="lg:col-span-5 relative self-start mt-16"
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.98 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5, delay: 0.4 }}
        className="relative w-full max-w-[500px] h-[400px] rounded-2xl overflow-hidden border-2 border-slate-200 shadow-lg bg-white"
      >
        <img 
          src="/images/hero.webp" // Use WebP format
          alt="ZuriIntel Dashboard"
          className="w-full h-full object-cover"
          loading="lazy" // Lazy-load image
        />
        <div className="absolute inset-0 bg-gradient-to-t from-white/10 via-transparent to-transparent pointer-events-none" />
      </motion.div>

      {!prefersReducedMotion && (
        <div className="relative mt-6 overflow-hidden">
          <motion.div
            animate={{ x: [0, -400] }}
            transition={{ duration: 15, repeat: Infinity, ease: "linear" }}
            className="flex gap-3"
          >
            {[...socialIcons, ...socialIcons].map((social, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 + (idx % 4) * 0.05 }}
                className="flex-shrink-0"
              >
                <div className={`w-12 h-12 rounded-lg bg-gradient-to-br ${social.color} flex items-center justify-center text-white border border-white`}>
                  <social.Icon className="text-xl" />
                </div>
              </motion.div>
            ))}
          </motion.div>
          <div className="absolute inset-y-0 left-0 w-16 bg-gradient-to-r from-white to-transparent pointer-events-none" />
          <div className="absolute inset-y-0 right-0 w-16 bg-gradient-to-l from-white to-transparent pointer-events-none" />
        </div>
      )}

      {prefersReducedMotion && (
        <div className="mt-6 flex flex-wrap gap-3 justify-center">
          {socialIcons.map((social, idx) => (
            <div
              key={idx}
              className={`w-12 h-12 rounded-lg bg-gradient-to-br ${social.color} flex items-center justify-center text-white border border-white`}
            >
              <social.Icon className="text-xl" />
            </div>
          ))}
        </div>
      )}
    </motion.div>
  );
});

export default Hero;